import { NativeHealthMetricValue, NativeHealthSection, NativeHealthState } from "@/utils/healthBridgeSchemas";

export interface HealthPocMetric {
  key: string;
  label: string;
  value: number;
  unit: string | null;
  measuredAt: string | null;
  available: boolean;
}

export interface HealthPocSection {
  key: string;
  title: string;
  metrics: HealthPocMetric[];
}

export interface HealthPocDailyHistoryEntry {
  date: string;
  steps: number | null;
}

export interface HealthPocWeeklyStep {
  date: string;
  label: string;
  value: number;
  available: boolean;
}

export interface HealthPocViewModel {
  source: string | null;
  sections: HealthPocSection[];
  workouts: NonNullable<NativeHealthState["summary"]>["workouts"];
  weeklySteps: HealthPocWeeklyStep[];
  historyAvailable: boolean;
}

export interface HealthPocViewOptions {
  today?: Date;
  dailyHistory?: HealthPocDailyHistoryEntry[];
}

interface KnownMetricDefinition {
  key: string;
  label: string;
  unit: string;
  summaryKey: keyof NonNullable<NativeHealthState["summary"]>;
  aliases: string[];
}

const knownMetrics: KnownMetricDefinition[] = [
  {
    key: "steps",
    label: "Passos",
    unit: "count",
    summaryKey: "steps",
    aliases: ["steps", "step", "passos", "stepcount"]
  },
  {
    key: "distance",
    label: "Distância",
    unit: "m",
    summaryKey: "distance",
    aliases: ["distance", "distancia", "distancemeters", "distancekm"]
  },
  {
    key: "energy",
    label: "Energia ativa",
    unit: "kcal",
    summaryKey: "activeEnergy",
    aliases: ["energy", "activeenergy", "calories", "activecalories", "energiaativa"]
  },
  { key: "weight", label: "Peso", unit: "kg", summaryKey: "weight", aliases: ["weight", "peso"] },
  {
    key: "sleep",
    label: "Sono",
    unit: "min",
    summaryKey: "sleepMinutes",
    aliases: ["sleep", "sleepminutes", "sleepduration", "sono"]
  },
  {
    key: "heart",
    label: "Frequência cardíaca",
    unit: "bpm",
    summaryKey: "heartRate",
    aliases: ["heart", "heartrate", "heartratebpm", "frequenciacardiaca"]
  },
  {
    key: "resting",
    label: "Frequência em repouso",
    unit: "bpm",
    summaryKey: "restingHeartRate",
    aliases: ["resting", "restingheartrate", "heartrateresting", "frequenciaemrepouso"]
  },
  {
    key: "oxygen",
    label: "Oxigenação",
    unit: "%",
    summaryKey: "oxygenSaturation",
    aliases: ["oxygen", "oxygensaturation", "spo2", "oxigenacao"]
  }
];

const normalizeKey = (key: string): string =>
  key
    .toLocaleLowerCase("pt-BR")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "");

const aliasToKnownMetric = new Map<string, string>();
knownMetrics.forEach(metric => {
  [metric.key, ...metric.aliases].forEach(alias => aliasToKnownMetric.set(normalizeKey(alias), metric.key));
});

const metricValue = (metric: NativeHealthMetricValue | null | undefined): HealthPocMetric["value"] =>
  metric?.value !== null && metric?.value !== undefined && Number.isFinite(metric.value) ? metric.value : 0;

const toPocMetric = (
  key: string,
  label: string,
  metric: NativeHealthMetricValue | null | undefined,
  fallbackUnit: string | null = null
): HealthPocMetric => {
  const available = metric?.value !== null && metric?.value !== undefined && Number.isFinite(metric.value);
  return {
    key,
    label,
    value: metricValue(metric),
    unit: metric?.unit ?? fallbackUnit,
    measuredAt: metric?.measuredAt ?? null,
    available
  };
};

const toDynamicMetric = (metric: NativeHealthSection["metrics"][number]): HealthPocMetric =>
  toPocMetric(metric.key, metric.label, metric.value);

const metricForSummaryKey = (
  summary: NonNullable<NativeHealthState["summary"]>,
  key: KnownMetricDefinition["summaryKey"]
): NativeHealthMetricValue | null => {
  const value = summary[key];
  return value && typeof value === "object" && "value" in value ? (value as NativeHealthMetricValue) : null;
};

const startOfDay = (date: Date): Date => new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));

const dateKey = (date: Date): string => date.toISOString().slice(0, 10);

const weeklyChart = (
  summary: NativeHealthState["summary"] | undefined,
  options: HealthPocViewOptions
): { points: HealthPocWeeklyStep[]; historyAvailable: boolean } => {
  const today = startOfDay(options.today ?? new Date());
  const dates = Array.from({ length: 7 }, (_, index) => {
    const date = new Date(today);
    date.setUTCDate(today.getUTCDate() - 6 + index);
    return dateKey(date);
  });
  const history = new Map<string, number>();

  [...(summary?.dailyHistory ?? []), ...(options.dailyHistory ?? [])].forEach(entry => {
    if (/^\d{4}-\d{2}-\d{2}$/.test(entry.date) && entry.steps !== null && Number.isFinite(entry.steps)) {
      history.set(entry.date, entry.steps);
    }
  });

  // Older native versions expose only the latest daily total. Use its own
  // period, never the summary date shared by unrelated measurements.
  const steps = summary?.steps;
  const timestamp = steps?.periodStart ?? steps?.measuredAt;
  if (timestamp && steps?.value !== null && steps?.value !== undefined && Number.isFinite(steps.value)) {
    const measuredDay = new Date(timestamp);
    if (!Number.isNaN(measuredDay.getTime())) {
      if (
        !steps.periodStart &&
        measuredDay.getHours() === 0 &&
        measuredDay.getMinutes() === 0 &&
        measuredDay.getSeconds() === 0
      ) {
        measuredDay.setTime(measuredDay.getTime() - 1);
      }
      const key = dateKey(startOfDay(measuredDay));
      if (!history.has(key)) history.set(key, steps.value);
    }
  }

  const dateFormatter = new Intl.DateTimeFormat("pt-BR", { weekday: "short", day: "2-digit", timeZone: "UTC" });
  const points = dates.map(date => {
    const value = history.get(date);
    const pointDate = new Date(`${date}T12:00:00.000Z`);
    return {
      date,
      label: dateFormatter.format(pointDate),
      value: value ?? 0,
      available: value !== undefined
    };
  });

  return {
    points,
    historyAvailable: points.some(point => point.available)
  };
};

/** Convert the bridge's evolving summary contract into stable view data for the health POC. */
export const adaptHealthPocView = (
  state: NativeHealthState | null | undefined,
  options: HealthPocViewOptions = {}
): HealthPocViewModel => {
  const summary = state?.summary;
  const sdkSections = summary?.sections ?? [];
  const dynamicKnownMetricKeys = new Set<string>();
  const dynamicSections: HealthPocSection[] = sdkSections.map(section => ({
    key: section.key,
    title: section.title,
    metrics: section.metrics.map(metric => {
      const keyFromMetric = aliasToKnownMetric.get(normalizeKey(metric.key));
      const keyFromSection =
        section.metrics.length === 1 ? aliasToKnownMetric.get(normalizeKey(section.key)) : undefined;
      const knownKey = keyFromMetric ?? keyFromSection;
      const definition = knownMetrics.find(item => item.key === knownKey);
      if (!definition) return toDynamicMetric(metric);

      dynamicKnownMetricKeys.add(definition.key);
      return toPocMetric(definition.key, metric.label, metric.value, definition.unit);
    })
  }));

  const legacyMetrics = knownMetrics
    .filter(definition => !dynamicKnownMetricKeys.has(definition.key))
    .map(definition => {
      const legacyMetric = summary ? metricForSummaryKey(summary, definition.summaryKey) : null;
      return toPocMetric(definition.key, definition.label, legacyMetric, definition.unit);
    });
  const legacySection: HealthPocSection[] = legacyMetrics.length
    ? [{ key: "known-metrics", title: "Resumo", metrics: legacyMetrics }]
    : [];

  const chart = weeklyChart(summary, options);
  return {
    source: state?.connectorId ?? null,
    sections: [...legacySection, ...dynamicSections],
    workouts: summary?.workouts ?? [],
    weeklySteps: chart.points,
    historyAvailable: chart.historyAvailable
  };
};
